import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Settings2, Plus, Pencil, Trash2, TestTube2, Check, X, Eye, EyeOff, Database, Loader2, Webhook, Copy, Globe, User } from 'lucide-react';
import { Project } from '@/types';
import { ProjectsTab } from './Projects';
import { ApiKeysTab } from './ApiKeys';
import { UsersTab } from './Users';

const PROVIDER_TYPES = [
  { value: 'usebouncer', label: 'UseBouncer' },
  { value: 'brevo', label: 'Brevo' },
];

interface DomainMapping {
  domain: string;
  projectId: string;
}

interface Provider {
  id: string;
  name: string;
  type: string;
  api_url: string;
  is_active: number;
  is_default: number;
  has_api_key: number;
  settings: string | null;
  created_at: string;
}

function ProvidersTab() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState<Provider | null>(null);
  const [domainMappings, setDomainMappings] = useState<DomainMapping[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    type: 'usebouncer',
    apiKey: '',
    apiUrl: 'https://api.usebouncer.com/v1.1',
    isDefault: false,
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; success: boolean; message: string } | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [providersData, projectsData] = await Promise.all([
        api.getProviders(),
        api.getProjects(true), // includeHidden - показываем все проекты для маппинга доменов
      ]);
      setProviders(providersData.providers);
      setProjects(projectsData.projects);
    } catch {
      // Silently fail
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const { providers } = await api.getProviders();
      setProviders(providers);
    } catch {
      // Silently fail
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      let settings: Record<string, any> | undefined;
      if (formData.type === 'brevo') {
        const validMappings = domainMappings.filter(m => m.domain && m.projectId);
        if (validMappings.length > 0) {
          const map: Record<string, string> = {};
          for (const m of validMappings) {
            map[m.domain.toLowerCase().trim()] = m.projectId;
          }
          settings = { domainProjectMap: map };
        }
      }

      if (editingProvider) {
        const updates: Record<string, any> = {
          name: formData.name,
          isDefault: formData.isDefault,
        };
        if (formData.apiKey) {
          updates.apiKey = formData.apiKey;
        }
        if (formData.apiUrl && formData.type !== 'brevo') {
          updates.apiUrl = formData.apiUrl;
        }
        if (settings) {
          updates.settings = settings;
        }
        await api.updateProvider(editingProvider.id, updates);
      } else {
        await api.createProvider({ ...formData, settings });
      }
      setShowModal(false);
      setEditingProvider(null);
      resetForm();
      fetchProviders();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Operation failed';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this provider?')) return;
    try {
      await api.deleteProvider(id);
      fetchProviders();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Delete failed';
      alert(message);
    }
  };

  const handleTest = async (id: string) => {
    setTesting(id);
    setTestResult(null);
    try {
      const result = await api.testProvider(id);
      let message: string;
      if (result.success) {
        message = result.credits !== undefined
          ? `Connected! Credits: ${result.credits}`
          : `Connected! ${result.email || ''} ${result.companyName ? `(${result.companyName})` : ''}`.trim();
      } else {
        message = result.error || 'Connection failed';
      }
      setTestResult({ id, success: result.success, message });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Test failed';
      setTestResult({ id, success: false, message });
    } finally {
      setTesting(null);
    }
  };

  const handleToggleActive = async (provider: Provider) => {
    try {
      await api.updateProvider(provider.id, { isActive: !provider.is_active });
      fetchProviders();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Update failed';
      alert(message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'usebouncer',
      apiKey: '',
      apiUrl: 'https://api.usebouncer.com/v1.1',
      isDefault: false,
    });
    setDomainMappings([{ domain: '', projectId: '' }]);
    setShowApiKey(false);
  };

  const openCreateModal = () => {
    setEditingProvider(null);
    resetForm();
    setError('');
    setShowModal(true);
  };

  const openEditModal = (provider: Provider) => {
    setEditingProvider(provider);
    const mappings: DomainMapping[] = [];
    if (provider.settings) {
      try {
        const s = typeof provider.settings === 'string' ? JSON.parse(provider.settings) : provider.settings;
        if (s.domainProjectMap) {
          for (const [domain, projectId] of Object.entries(s.domainProjectMap)) {
            mappings.push({ domain, projectId: projectId as string });
          }
        }
      } catch { /* ignore */ }
    }
    if (mappings.length === 0) mappings.push({ domain: '', projectId: '' });
    setDomainMappings(mappings);
    setFormData({
      name: provider.name,
      type: provider.type,
      apiKey: '',
      apiUrl: provider.api_url || 'https://api.usebouncer.com/v1.1',
      isDefault: provider.is_default === 1,
    });
    setShowApiKey(false);
    setError('');
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Verification Providers</CardTitle>
              <CardDescription>
                Manage API providers for email verification
              </CardDescription>
            </div>
            <Button onClick={openCreateModal}>
              <Plus className="mr-2 h-4 w-4" />
              Add Provider
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {providers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Settings2 className="h-12 w-12 text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">No providers configured</p>
              <Button onClick={openCreateModal} className="mt-4" variant="outline">
                <Plus className="mr-2 h-4 w-4" />
                Add your first provider
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>API Key</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead className="w-[200px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {providers.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{p.type}</Badge>
                    </TableCell>
                    <TableCell>
                      {p.has_api_key ? (
                        <Badge variant="success">Configured</Badge>
                      ) : (
                        <Badge variant="destructive">Not set</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={p.is_active ? 'success' : 'secondary'}>
                        {p.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {p.is_default ? (
                        <Check className="h-4 w-4 text-emerald-600" />
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleTest(p.id)}
                          disabled={testing === p.id || !p.has_api_key}
                        >
                          <TestTube2 className="h-4 w-4 mr-1" />
                          {testing === p.id ? 'Testing...' : 'Test'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditModal(p)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleActive(p)}
                        >
                          {p.is_active ? (
                            <X className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Check className="h-4 w-4 text-emerald-600" />
                          )}
                        </Button>
                        {!p.is_default && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(p.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                      {testResult?.id === p.id && (
                        <div
                          className={`mt-2 text-xs ${
                            testResult.success ? 'text-emerald-600' : 'text-destructive'
                          }`}
                        >
                          {testResult.message}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingProvider ? 'Edit Provider' : 'Add Provider'}
            </DialogTitle>
            <DialogDescription>
              {editingProvider
                ? 'Update provider configuration'
                : 'Add a new verification provider'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="providerName">Name</Label>
                <Input
                  id="providerName"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="UseBouncer Production"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Type</Label>
                {editingProvider ? (
                  <Input value={formData.type} disabled className="bg-muted" />
                ) : (
                  <Select
                    value={formData.type}
                    onValueChange={(value) => setFormData({
                      ...formData,
                      type: value,
                      apiUrl: value === 'usebouncer' ? 'https://api.usebouncer.com/v1.1' : '',
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROVIDER_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {formData.type === 'usebouncer' && (
                <div className="space-y-2">
                  <Label htmlFor="apiUrl">API URL</Label>
                  <Input
                    id="apiUrl"
                    value={formData.apiUrl}
                    onChange={(e) => setFormData({ ...formData, apiUrl: e.target.value })}
                    placeholder="https://api.usebouncer.com/v1.1"
                  />
                </div>
              )}

              {formData.type === 'brevo' && (
                <div className="space-y-2">
                  <Label>Sender domain &rarr; Project</Label>
                  <div className="space-y-2">
                    {domainMappings.map((mapping, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          value={mapping.domain}
                          onChange={(e) => {
                            const updated = [...domainMappings];
                            updated[index] = { ...updated[index], domain: e.target.value };
                            setDomainMappings(updated);
                          }}
                          placeholder="megazip.net"
                          className="flex-1"
                        />
                        <span className="text-muted-foreground text-sm shrink-0">&rarr;</span>
                        <Select
                          value={mapping.projectId}
                          onValueChange={(value) => {
                            const updated = [...domainMappings];
                            updated[index] = { ...updated[index], projectId: value };
                            setDomainMappings(updated);
                          }}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Project" />
                          </SelectTrigger>
                          <SelectContent>
                            {projects.map((p) => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {domainMappings.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="shrink-0"
                            onClick={() => setDomainMappings(domainMappings.filter((_, i) => i !== index))}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setDomainMappings([...domainMappings, { domain: '', projectId: '' }])}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add domain
                  </Button>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="apiKey">
                  API Key {editingProvider && '(leave empty to keep current)'}
                </Label>
                <div className="relative">
                  <Input
                    id="apiKey"
                    type={showApiKey ? 'text' : 'password'}
                    value={formData.apiKey}
                    onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                    placeholder={editingProvider ? '••••••••' : 'Enter API key'}
                    {...(!editingProvider && { required: true })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label htmlFor="isDefault" className="font-normal">
                  Set as default provider
                </Label>
              </div>

              {error && <div className="text-sm text-destructive">{error}</div>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving...' : editingProvider ? 'Save' : 'Add Provider'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function formatBytes(bytes: number) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function PostgresCard() {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<{
    configured: boolean;
    connected: boolean;
    version?: string;
    writeAccess?: boolean;
    writeError?: string;
    schemaComplete?: boolean;
    missingTables?: string[];
    tables?: Array<{ name: string; rows: number; sizeBytes: number }>;
    counts?: Record<string, number>;
    message: string;
  } | null>(null);
  const [error, setError] = useState('');

  const handleCheck = async () => {
    setChecking(true);
    setError('');
    setResult(null);
    try {
      const res = await api.checkPostgres();
      setResult(res);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Check failed');
    } finally {
      setChecking(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>PostgreSQL</CardTitle>
            <CardDescription>
              Primary database via Hyperdrive
            </CardDescription>
          </div>
          <Button onClick={handleCheck} disabled={checking}>
            {checking ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Database className="mr-2 h-4 w-4" />
            )}
            {checking ? 'Checking...' : 'Check Connection'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {error && <div className="text-sm text-destructive mb-4">{error}</div>}

        {result && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge variant={result.connected ? 'success' : 'destructive'}>
                {result.connected ? 'Connected' : 'Disconnected'}
              </Badge>
              {result.connected && (
                <>
                  <Badge variant={result.schemaComplete ? 'success' : 'destructive'}>
                    {result.schemaComplete ? 'Schema OK' : 'Schema Incomplete'}
                  </Badge>
                  <Badge variant={result.writeAccess ? 'success' : 'destructive'}>
                    {result.writeAccess ? 'Write OK' : 'Write Failed'}
                  </Badge>
                </>
              )}
            </div>

            <div className={`text-sm ${result.connected && result.schemaComplete && result.writeAccess ? 'text-emerald-600' : 'text-muted-foreground'}`}>
              {result.message}
            </div>

            {result.version && (
              <div className="text-xs text-muted-foreground font-mono">{result.version}</div>
            )}

            {result.writeError && (
              <div className="text-sm text-destructive">{result.writeError}</div>
            )}

            {result.missingTables && result.missingTables.length > 0 && (
              <div className="text-sm">
                <span className="font-medium text-destructive">Missing tables:</span>{' '}
                {result.missingTables.join(', ')}
              </div>
            )}

            {result.counts && Object.keys(result.counts).length > 0 && (
              <div className="text-sm space-y-1">
                <span className="font-medium">Row counts:</span>
                {Object.entries(result.counts).map(([table, count]) => (
                  <div key={table} className="ml-4">
                    {table}: {count >= 0 ? count.toLocaleString() : 'error'}
                  </div>
                ))}
              </div>
            )}

            {result.tables && result.tables.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Table</TableHead>
                    <TableHead className="text-right">Rows (est.)</TableHead>
                    <TableHead className="text-right">Size</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.tables.map((t) => (
                    <TableRow key={t.name}>
                      <TableCell className="font-mono text-sm">{t.name}</TableCell>
                      <TableCell className="text-right">{t.rows.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{formatBytes(t.sizeBytes)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}

        {!result && !error && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Database className="h-12 w-12 text-muted-foreground/50" />
            <p className="mt-4 text-muted-foreground">Click "Check Connection" to verify PostgreSQL</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ClickHouseCard() {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<{
    configured: boolean;
    connected: boolean;
    writeAccess?: boolean;
    writeError?: string;
    schemaComplete?: boolean;
    missingTables?: string[];
    tables?: Array<{ name: string; engine: string; rows: number; sizeBytes: number }>;
    counts?: Record<string, number>;
    message: string;
  } | null>(null);
  const [error, setError] = useState('');

  const handleCheck = async () => {
    setChecking(true);
    setError('');
    setResult(null);
    try {
      const res = await api.checkClickHouse();
      setResult(res);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Check failed');
    } finally {
      setChecking(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>ClickHouse</CardTitle>
            <CardDescription>
              Analytics database for brevo_events, verification_logs, api_logs
            </CardDescription>
          </div>
          <Button onClick={handleCheck} disabled={checking}>
            {checking ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Database className="mr-2 h-4 w-4" />
            )}
            {checking ? 'Checking...' : 'Check Connection'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {error && <div className="text-sm text-destructive mb-4">{error}</div>}

        {result && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge variant={result.configured ? 'success' : 'destructive'}>
                {result.configured ? 'Configured' : 'Not Configured'}
              </Badge>
              {result.configured && (
                <Badge variant={result.connected ? 'success' : 'destructive'}>
                  {result.connected ? 'Connected' : 'Disconnected'}
                </Badge>
              )}
              {result.connected && (
                <>
                  <Badge variant={result.schemaComplete ? 'success' : 'destructive'}>
                    {result.schemaComplete ? 'Schema OK' : 'Schema Incomplete'}
                  </Badge>
                  <Badge variant={result.writeAccess ? 'success' : 'destructive'}>
                    {result.writeAccess ? 'Write OK' : 'Write Failed'}
                  </Badge>
                </>
              )}
            </div>

            <div className={`text-sm ${result.connected && result.schemaComplete && result.writeAccess ? 'text-emerald-600' : 'text-muted-foreground'}`}>
              {result.message}
            </div>

            {result.writeError && (
              <div className="text-sm text-destructive">{result.writeError}</div>
            )}

            {result.missingTables && result.missingTables.length > 0 && (
              <div className="text-sm">
                <span className="font-medium text-destructive">Missing tables:</span>{' '}
                {result.missingTables.join(', ')}
              </div>
            )}

            {result.counts && Object.keys(result.counts).length > 0 && (
              <div className="text-sm space-y-1">
                <span className="font-medium">Row counts:</span>
                {Object.entries(result.counts).map(([table, count]) => (
                  <div key={table} className="ml-4">
                    {table}: {count >= 0 ? count.toLocaleString() : 'error'}
                  </div>
                ))}
              </div>
            )}

            {result.tables && result.tables.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Table</TableHead>
                    <TableHead>Engine</TableHead>
                    <TableHead className="text-right">Rows</TableHead>
                    <TableHead className="text-right">Size</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.tables.map((t) => (
                    <TableRow key={t.name}>
                      <TableCell className="font-mono text-sm">{t.name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{t.engine}</Badge>
                      </TableCell>
                      <TableCell className="text-right">{t.rows.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{formatBytes(t.sizeBytes)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}

        {!result && !error && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Database className="h-12 w-12 text-muted-foreground/50" />
            <p className="mt-4 text-muted-foreground">Click "Check Connection" to verify ClickHouse</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface BrevoWebhook {
  id: number;
  url: string;
  description: string;
  events: string[];
  type: string;
  createdAt: string;
  modifiedAt: string;
}

function BrevoWebhooksCard() {
  const [loading, setLoading] = useState(false);
  const [webhooks, setWebhooks] = useState<BrevoWebhook[]>([]);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  const webhookUrl = 'https://email-core-api.s25.app/api/webhooks/brevo';

  const fetchWebhooks = async () => {
    setLoading(true);
    setError('');
    try {
      const { webhooks } = await api.getBrevoWebhooks();
      setWebhooks(webhooks);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      await api.createBrevoWebhook(webhookUrl);
      setShowCreate(false);
      fetchWebhooks();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create webhook');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this webhook?')) return;
    setDeleting(id);
    setError('');
    try {
      await api.deleteBrevoWebhook(id);
      fetchWebhooks();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete webhook');
    } finally {
      setDeleting(null);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Brevo Webhooks</CardTitle>
            <CardDescription>
              Webhooks for real-time email event notifications
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchWebhooks} disabled={loading}>
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Webhook className="mr-2 h-4 w-4" />
              )}
              {loading ? 'Loading...' : 'Load'}
            </Button>
            <Button onClick={() => setShowCreate(true)} disabled={loading}>
              <Plus className="mr-2 h-4 w-4" />
              Create
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && <div className="text-sm text-destructive mb-4">{error}</div>}

        {showCreate && (
          <div className="mb-4 p-4 border rounded-lg bg-muted/50">
            <div className="space-y-3">
              <div>
                <Label>Webhook URL</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={webhookUrl} readOnly className="font-mono text-sm" />
                  <Button variant="outline" size="icon" onClick={handleCopy}>
                    {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Events: sent, delivered, hardBounce, softBounce, blocked, spam, invalid, deferred, click, opened, uniqueOpened, unsubscribed
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreate} disabled={creating}>
                  {creating ? 'Creating...' : 'Create Webhook'}
                </Button>
                <Button variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {webhooks.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Events</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {webhooks.map((wh) => (
                <TableRow key={wh.id}>
                  <TableCell className="font-mono">{wh.id}</TableCell>
                  <TableCell className="font-mono text-sm max-w-[300px] truncate" title={wh.url}>
                    {wh.url}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {wh.events.slice(0, 3).map((e) => (
                        <Badge key={e} variant="secondary" className="text-xs">{e}</Badge>
                      ))}
                      {wh.events.length > 3 && (
                        <Badge variant="outline" className="text-xs">+{wh.events.length - 3}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(wh.id)}
                      disabled={deleting === wh.id}
                    >
                      {deleting === wh.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 text-destructive" />
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : !loading && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Webhook className="h-12 w-12 text-muted-foreground/50" />
            <p className="mt-4 text-muted-foreground">Click "Load" to fetch webhooks from Brevo</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SystemTab() {
  return (
    <div className="space-y-6">
      <PostgresCard />
      <ClickHouseCard />
      <BrevoWebhooksCard />
    </div>
  );
}

const COMMON_TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/Moscow', label: 'Moscow (UTC+3)' },
  { value: 'Europe/London', label: 'London (UTC+0/+1)' },
  { value: 'Europe/Paris', label: 'Paris (UTC+1/+2)' },
  { value: 'Europe/Berlin', label: 'Berlin (UTC+1/+2)' },
  { value: 'America/New_York', label: 'New York (UTC-5/-4)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (UTC-8/-7)' },
  { value: 'America/Chicago', label: 'Chicago (UTC-6/-5)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (UTC+8)' },
  { value: 'Asia/Singapore', label: 'Singapore (UTC+8)' },
  { value: 'Asia/Bangkok', label: 'Bangkok (UTC+7)' },
  { value: 'Asia/Dubai', label: 'Dubai (UTC+4)' },
  { value: 'Asia/Kolkata', label: 'India (UTC+5:30)' },
  { value: 'Australia/Sydney', label: 'Sydney (UTC+10/+11)' },
  { value: 'Pacific/Auckland', label: 'Auckland (UTC+12/+13)' },
];

function ProfileTab() {
  const { user, setUser } = useAuth();
  const [timezone, setTimezone] = useState(user?.timezone || 'UTC');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const updated = await api.updateProfile({ timezone });
      setUser({ ...user!, timezone: updated.timezone });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const currentTime = new Date().toLocaleString('en-GB', {
    timeZone: timezone,
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Profile Settings
        </CardTitle>
        <CardDescription>
          Manage your personal preferences
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label>Email</Label>
          <Input value={user?.email || ''} disabled className="bg-muted" />
        </div>

        <div className="space-y-2">
          <Label>Role</Label>
          <Input value={user?.role || ''} disabled className="bg-muted capitalize" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="timezone" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Timezone
          </Label>
          <Select value={timezone} onValueChange={setTimezone}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COMMON_TIMEZONES.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-sm text-muted-foreground">
            Current time: {currentTime}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={handleSave} disabled={saving || timezone === user?.timezone}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : saved ? (
              <>
                <Check className="mr-2 h-4 w-4" />
                Saved
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

const TAB_VALUES = ['profile', 'providers', 'projects', 'api-keys', 'users', 'system'] as const;
type TabValue = typeof TAB_VALUES[number];

export default function Settings() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = user?.role === 'admin';

  const tabParam = searchParams.get('tab') as TabValue | null;
  const activeTab: TabValue = tabParam && TAB_VALUES.includes(tabParam) ? tabParam : 'profile';

  const handleTabChange = (value: string) => {
    if (value === 'profile') {
      setSearchParams({});
    } else {
      setSearchParams({ tab: value });
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Manage providers, projects, API keys and users</p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="providers">Providers</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="api-keys">API Keys</TabsTrigger>
          {isAdmin && <TabsTrigger value="users">Users</TabsTrigger>}
          {isAdmin && <TabsTrigger value="system">System</TabsTrigger>}
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <ProfileTab />
        </TabsContent>

        <TabsContent value="providers" className="mt-6">
          <ProvidersTab />
        </TabsContent>

        <TabsContent value="projects" className="mt-6">
          <ProjectsTab />
        </TabsContent>

        <TabsContent value="api-keys" className="mt-6">
          <ApiKeysTab />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="users" className="mt-6">
            <UsersTab />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="system" className="mt-6">
            <SystemTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
